/**
 * One academy's promotion phase switches, for the platform console.
 *
 * THE OWNING APP IS THE ONLY WRITER. `promo_codes_enabled` and `access_codes_enabled` are
 * columns in `openedx_thinlms_promotions`' own `TenantPromotionSettings` table. They are NOT
 * `config/features.yaml` flags, so the tenancy app cannot validate them, and NOT
 * `TenantStripeAccount` columns, so the payments endpoint writing them would be a second writer
 * for one field. This module therefore talks to `/api/promotions/v1/tenants/<slug>/settings` and
 * nothing else — the same reasoning `PaymentsCard`'s header records for `paid_enrollment`,
 * applied to a field with a different owner.
 *
 * WHY THE SLUG IS IN THE PATH HERE AND NOT ON THE ACADEMY'S OWN PROMOTIONS ROUTES. Everything
 * else in that app resolves the academy from the request Host. The console runs on a host that
 * is deliberately not an academy, so the Host-scoped route answers `404 no_tenant` for it. That
 * route still exists and is what an academy admin's own screens use; this is its console
 * counterpart, with its own gate.
 *
 * `changed` IS ON THE PATCH RESPONSE AND IT IS NOT DECORATION. "It was already off" and "it has
 * been switched off" are different answers, and a console that cannot tell them apart shows a
 * success for a write that did nothing.
 */

import { openedx } from '../../shared/lib/openedx';

/** Can discount codes be minted right now — and the sentence to show when they cannot. */
export interface PromotionsStripeReadiness {
  ready: boolean;
  /** The Stripe account state as the promotions app read it. `not_connected` when there is none. */
  state: string;
  /** The server's own sentence. Empty when `ready` — there is nothing to explain. */
  message: string;
}

export interface PromotionSwitches {
  tenant_slug: string;
  promo_codes_enabled: boolean;
  access_codes_enabled: boolean;
  stripe_ready: PromotionsStripeReadiness;
  /**
   * Whether a row exists for this academy at all.
   *
   * `false` here means NOBODY HAS EVER SET THIS, which is a different fact from somebody having
   * switched it off — a fail-closed default, not a decision. The card says so, in the same
   * three-valued spirit as `paidEnrolmentIsDefault`.
   */
  explicitly_set: boolean;
}

export interface PromotionSwitchWrite extends PromotionSwitches {
  /** False when every value sent already held. Not an error and not a success — a no-op. */
  changed: boolean;
}

const url = (slug: string) =>
  `/api/promotions/v1/tenants/${encodeURIComponent(slug)}/settings`;

export const promotionsApi = {
  get: (slug: string) => openedx.get<PromotionSwitches>(url(slug)),

  /**
   * Either switch, or both. A body with neither is refused by the server, by name.
   *
   * Booleans only: the server refuses a string rather than coercing it, because `bool("false")`
   * is `true` and that is the direction that switches a money feature ON for an operator who
   * was switching it off.
   */
  setSwitches: (
    slug: string,
    switches: { promo_codes_enabled?: boolean; access_codes_enabled?: boolean },
  ) => openedx.patch<PromotionSwitchWrite>(url(slug), switches),
};
